/**
 * Check if an element or any of its ancestors has a class name containing the given class name.
 * Return the first ancestor that has the class name.
 * @param element
 * @param className
 */
export function getElementOrAncestorWithClass(
  element: HTMLElement,
  className: string
) {
  return findElementOrAncestor(element, (element) =>
    element.classList.contains(className)
  );
}

/**
 * Check if an element or any of its ancestors has a specific tag name.
 * Return the first ancestor that matches the tag.
 * @param element
 * @param tagName
 */
export function getElementOrAncestorWithTag(
  element: HTMLElement,
  tagName: string
) {
  return findElementOrAncestor(
    element,
    (element) => element.tagName == tagName
  );
}

/**
 * Check if an element or any of its ancestors fulfills the given predicate.
 * Return the element fullfilling the predicate.
 * @param element
 * @param check
 */
export function findElementOrAncestor(
  element: HTMLElement,
  check: (element: HTMLElement) => boolean
) {
  while (element) {
    if (check(element)) {
      return element;
    }
    if (element.parentElement == null) return null;
    element = element.parentElement;
  }
  return null;
}

/**
 * Return a selector chain for the given element.
 * @param element
 * @param baseElement
 */
export function getRelativeSelector(
  element: Node | Element,
  baseElement: HTMLElement
) {
  let selector = "";
  while (element && element !== baseElement) {
    if (element instanceof Element) {
      const classList = Array.from(element?.classList ?? []);
      if (classList && classList.length > 0) {
        selector = ` ${element.tagName}.${classList.join(".")}${selector}`;
      }
    }
    if (element.parentElement == null) return selector;
    element = element.parentElement;
  }
  return selector;
}

/**
 * @param element
 */
export function hasNeighbouringTextChildNodes(element: Node) {
  if (element.nodeType !== Node.ELEMENT_NODE) {
    return false;
  }

  const childNodes = Array.from(element.childNodes);
  if (childNodes.length < 2) return false;

  let prevChildNode = null;
  for (const childNode of childNodes) {
    if (
      prevChildNode?.nodeType === Node.TEXT_NODE &&
      childNode.nodeType === Node.TEXT_NODE
    ) {
      return true;
    }

    if (hasNeighbouringTextChildNodes(childNode)) {
      return true;
    }
    prevChildNode = childNode;
  }

  return false;
}

export function removeAttributeRecursive(element: Element, attribute: string) {
  if (element.hasAttribute(attribute)) {
    element.removeAttribute(attribute);
  }

  const children = Array.from(element.children);
  for (const child of children) {
    removeAttributeRecursive(child, attribute);
  }
}

/**
 * Returns the next scrollable parent node
 * @returns {HTMLElement}
 */
export function getScrollParent(node: Node | null): HTMLElement | null {
  const isElement = node instanceof HTMLElement;
  const overflowY = isElement && window.getComputedStyle(node).overflowY;
  const isScrollable = overflowY !== "visible" && overflowY !== "hidden";

  if (!node) {
    return null;
  } else if (
    isScrollable &&
    isElement &&
    node.scrollHeight >= node.clientHeight
  ) {
    return node;
  }

  return getScrollParent(node.parentNode);
}

/**
 * Move upwards in the DOM tree and return the path, including the teleport parents.
 */
export function getComposedPathWithTeleport(composedPath: EventTarget[]) {
  const path = [];

  let element: Element | null = null;

  for (const node of composedPath) {
    if (!(node instanceof Element)) continue;

    path.push(node);
    if ("teleportParent" in node) {
      element = node["teleportParent"] as Element;
      break;
    }
  }

  while (element) {
    path.push(element);
    if (element.tagName === "HTML") {
      path.push(document);
      path.push(window);
      return path;
    }
    if ("teleportParent" in element) {
      element = element["teleportParent"] as Element | null;
    } else {
      element = element.parentElement;
    }
  }

  return path;
}


export function getAncestors(element: Element): HTMLElement[] {
  const ancestors: HTMLElement[] = [];
  let current: HTMLElement | null = element.parentElement;

  while (current) {
    ancestors.push(current);
    current = current.parentElement;
  }

  return ancestors;
}

export function findCommonAncestor(idA: string, idB: string): HTMLElement | null {
  const elementA = document.getElementById(idA);
  const elementB = document.getElementById(idB);

  if (!elementA || !elementB) return null;

  const ancestorsA = getAncestors(elementA);
  const ancestorsB = getAncestors(elementB);

  for (const ancestorA of ancestorsA) {
    if (ancestorsB.includes(ancestorA)) {
      return ancestorA;
    }
  }

  return null;
}